import * as z from 'zod';

/**
 * Which nav group is expanded, shared by the client sidebar that WRITES it and
 * the server layouts that READ it back.
 *
 * No `'use client'` directive here, deliberately — same reason as
 * `../shadcn/sidebar-cookie`. A constant imported from a client module into a
 * server component becomes an opaque client reference rather than the string,
 * and nothing in the type system or the test suite notices.
 *
 * Only one group is open at a time (the nav behaves as an accordion), so the
 * value is a single group key rather than a list.
 */

/** Cookie holding the open group's key, or absent when every group is closed. */
export const SIDEBAR_NAV_GROUP_COOKIE_NAME = 'sidebar_nav_group';

/** Session-length: the value is cleared on sign-out, not aged out. */
export const SIDEBAR_NAV_GROUP_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

/**
 * Explicit "everything closed" sentinel. Group keys always start with a letter,
 * so `0` can never collide with a real group.
 */
export const SIDEBAR_NAV_GROUP_CLOSED = '0';

/**
 * Group keys are i18n keys (`common.routes.fieldData`). The prefix carries no
 * information and would triple the cookie, so it is dropped on the way in and
 * never reconstructed — the key is only ever compared against other keys run
 * through this same function.
 */
export function toNavGroupKey(label: string): string {
  return label.replace(/^common\.routes\./, '');
}

/**
 * Parse the cookie into a group key. Anything unrecognised is treated as
 * absent (every group closed) rather than throwing: a stale or hand-edited
 * cookie must not be able to break the nav.
 */
export function parseNavGroupCookie(value: string | undefined): string | null {
  const parsed = z
    .union([
      z.literal(SIDEBAR_NAV_GROUP_CLOSED),
      // Keys come from i18n identifiers, so this is the full alphabet they can
      // use. The length cap keeps a hand-crafted cookie from bloating the
      // header.
      z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,63}$/),
    ])
    .safeParse(value);

  return parsed.success ? parsed.data : null;
}
